import { ChainIdLegacy, ChainKey, SupportedChainId } from "@silo/types";

const LOCAL_RPC_URL = "http://localhost:8545";
const ANVIL_RPC_URL = "https://anvil.silo.finance/";

/**
 * Chains that should bypass the same-origin proxy and hit a node directly
 * (there is no provider key to protect for these).
 */
const DIRECT_RPC_URLS: Partial<Record<SupportedChainId, string>> = {
  [ChainIdLegacy.LOCAL]: LOCAL_RPC_URL,
  [ChainIdLegacy.ANVIL]: ANVIL_RPC_URL,
};

/**
 * `chainKey` path segment used by the proxy route `/api/network/[chainKey]`
 * for each proxied chain.
 */
const PROXY_CHAIN_KEYS: Partial<Record<SupportedChainId, ChainKey>> = {
  [ChainIdLegacy.MAINNET]: "ethereum",
  [ChainIdLegacy.ARBITRUM]: "arbitrum",
  [ChainIdLegacy.OPTIMISM]: "optimism",
  [ChainIdLegacy.BASE]: "base",
};

/**
 * Base URL for the same-origin proxy. In the browser this is the current
 * origin; during SSR / module-load (web3-react connectors instantiate at
 * import time) it is an empty string, yielding a relative URL that ethers
 * resolves same-origin once a fetch actually fires in the browser.
 */
export function getProxyBaseUrl(): string {
  return typeof window !== "undefined" ? window.location.origin : "";
}

/**
 * Build the RPC URL a browser provider should use for a chain. Proxied chains
 * point at `${origin}/api/network/${chainKey}` so provider keys never leave the
 * server; local/anvil hit their node directly.
 */
export function getProxyRpcUrl(chainId: SupportedChainId): string {
  const direct = DIRECT_RPC_URLS[chainId];
  if (direct) {
    return direct;
  }

  const chainKey = PROXY_CHAIN_KEYS[chainId];
  if (!chainKey) {
    // Unreachable for a SupportedChainId; default to the mainnet proxy.
    return `${getProxyBaseUrl()}/api/network/ethereum`;
  }

  return `${getProxyBaseUrl()}/api/network/${chainKey}`;
}
